/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useTheme } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { hierarchy as d3hierarchy, tree as d3tree } from 'd3-hierarchy';

// Horizontal Reingold-Tilford tree: depth→x-axis, siblings→y-axis
const BLOCK_W = 120;
const BLOCK_H = 30;
const NODE_DX = 132;   // horizontal spacing between depth levels
const NODE_DY = 38;    // vertical spacing between siblings
const PAD_X = 10;
const PAD_Y = 6;


interface ProbeNode {
    id: number;
    parent_id?: number | string | null;
    desc?: string;
    title?: string;
    probe_type?: string;
    op_type?: string;
    op_name?: string;
    passed?: boolean;
    pending?: boolean;
    running?: boolean;
}

interface LayoutNode extends ProbeNode {
    x: number;           // horizontal position
    y: number;           // vertical position
    hasChildren: boolean;
    isCollapsed: boolean;
    childCount: number;
}

function buildChildMap(nodes: ProbeNode[]): Map<number, ProbeNode[]> {
    const m = new Map<number, ProbeNode[]>();
    for (const n of nodes) {
        const pid = n.parent_id ? Number(n.parent_id) : null;
        if (pid) {
            if (!m.has(pid)) m.set(pid, []);
            m.get(pid)!.push(n);
        }
    }
    return m;
}

function findRoot(nodes: ProbeNode[], childrenOf: Map<number, ProbeNode[]>): ProbeNode {
    const childIds = new Set<number>();
    for (const children of childrenOf.values()) {
        for (const c of children) childIds.add(c.id);
    }
    const roots = nodes.filter(n => !childIds.has(n.id));
    if (roots.length === 1) return roots[0];
    return (
        nodes.find(n => !n.parent_id) ??
        nodes.reduce((min, n) => n.id < min.id ? n : min, nodes[0])
    );
}

function layoutTree(
    nodes: ProbeNode[],
    collapsed: Set<number>,
): { layout: LayoutNode[]; width: number; height: number } {
    if (!nodes.length) return { layout: [], width: 0, height: 0 };

    const childrenOf = buildChildMap(nodes);
    const rootNode   = findRoot(nodes, childrenOf);

    const byId = new Map<number, ProbeNode>();
    nodes.forEach((n) => byId.set(n.id, n));

    type LayoutDatum = { id: number; children: LayoutDatum[] };
    function buildDatum(node: ProbeNode): LayoutDatum {
        const fullChildren = childrenOf.get(node.id) || [];
        const visibleChildren = collapsed.has(node.id)
            ? []
            : fullChildren.map((c) => buildDatum(c));
        return {
            id: node.id,
            children: visibleChildren,
        };
    }

    const rootDatum = buildDatum(rootNode);
    const hRoot = d3hierarchy<LayoutDatum>(rootDatum, (d) => d.children);
    d3tree<LayoutDatum>().nodeSize([NODE_DY, NODE_DX])(hRoot);

    const xs = hRoot.descendants().map((d: any) => d.y);
    const ys = hRoot.descendants().map((d: any) => d.x);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);

    const layout: LayoutNode[] = hRoot.descendants().map((d: any) => {
        const n = byId.get(d.data.id)!;
        const fullChildren = childrenOf.get(n.id) || [];
        return {
            ...n,
            x: d.y - minX + PAD_X,
            y: d.x - minY + PAD_Y,
            hasChildren: fullChildren.length > 0,
            isCollapsed: collapsed.has(n.id),
            childCount: fullChildren.length,
        };
    });

    const width = Math.max(1, (maxX - minX) + BLOCK_W + PAD_X * 2);
    const height = Math.max(1, (maxY - minY) + BLOCK_H + PAD_Y * 2);
    return { layout, width, height };
}


function nodeColor(node: ProbeNode, theme: any): string {
    if (node.running) return theme.colors.nodeRunning;
    if (node.pending) return theme.colors.nodePending;
    if (node.passed)  return theme.colors.nodeGood;
    return theme.colors.nodeBad;
}

// ─── per-Tree SVG (horizontal layout: depth→x, siblings→y) ─────────────────

interface TreeSvgProps {
    rootId: number;
    nodes: ProbeNode[];
    collapsed: Set<number>;
    onToggle: (id: number) => void;
    lastActiveId: number | null;
    selectedNodeId?: number | null;
    blockRefs: React.RefObject<Map<number, SVGRectElement>>;
    theme: any;
    navigate: (path: string) => void;
}

function TreeSvg({ rootId, nodes, collapsed, onToggle, lastActiveId, selectedNodeId, blockRefs, theme, navigate }: TreeSvgProps) {
    const { layout, width, height } = useMemo(
        () => layoutTree(nodes, collapsed),
        [nodes, collapsed],
    );

    const svgW = width;
    const svgH = height;

    const connectors = useMemo(() => {
        const byId = new Map<number, LayoutNode>();
        for (const n of layout) byId.set(n.id, n);

        return layout
            .filter(n => n.parent_id)
            .map(child => {
                const parent = byId.get(Number(child.parent_id));
                if (!parent) return null;
                // Connect right-edge of parent to left-edge of child (horizontal S-curve)
                const x1 = parent.x + BLOCK_W;
                const y1 = parent.y + BLOCK_H / 2;
                const x2 = child.x;
                const y2 = child.y + BLOCK_H / 2;
                const mx = (x1 + x2) / 2;
                return (
                    <path
                        key={`${parent.id}-${child.id}`}
                        d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`}
                        stroke={theme.colors.ctext}
                        strokeOpacity={0.3}
                        strokeWidth={1.5}
                        fill="none"
                    />
                );
            })
            .filter(Boolean);
    }, [layout, theme]);

    const TOGGLE_W = 18;
    const TOGGLE_H = 14;

    return (
        <div style={{ marginBottom: 20 }}>
            <div style={{
                fontSize: '0.7em',
                color: theme.colors.textMuted || '#888',
                marginBottom: 4,
                paddingLeft: PAD_X + 4,
            }}>
                tree #{rootId} — {nodes.length} node{nodes.length !== 1 ? 's' : ''}
            </div>
            <svg
                width={svgW}
                height={svgH}
                style={{ display: 'block', overflow: 'visible' }}
            >
                <defs>
                    {layout.map(n => {
                        const x = n.x;
                        const y = n.y;
                        const clipW = n.hasChildren ? BLOCK_W - TOGGLE_W - 10 : BLOCK_W - 12;
                        return (
                            <clipPath key={n.id} id={`cp-${rootId}-${n.id}`}>
                                <rect x={x + 6} y={y} width={clipW} height={BLOCK_H / 2 + 4} />
                            </clipPath>
                        );
                    })}
                </defs>

                {connectors}

                {layout.map(n => {
                    const x      = n.x;
                    const y      = n.y;
                    const color  = nodeColor(n, theme);
                    const active = n.id === lastActiveId;
                    const selected = n.id === selectedNodeId;
                    const strokeColor = selected ? (theme.colors.info || theme.colors.ctext || '#66d9ef') : color;
                    const fillColor = selected ? (theme.colors.bg2 || theme.colors.bg || '#111') : theme.colors.bg;

                    return (
                        <g key={n.id}>
                            {/* Main block — click → probe detail */}
                            <rect
                                ref={(el: SVGRectElement | null) => {
                                    if (el) blockRefs.current!.set(n.id, el);
                                    else blockRefs.current!.delete(n.id);
                                }}
                                x={x} y={y}
                                width={BLOCK_W} height={BLOCK_H}
                                rx={3} ry={3}
                                stroke={strokeColor}
                                strokeWidth={selected ? 3 : active ? 2.5 : 1.5}
                                fill={fillColor}
                                style={{ cursor: 'pointer' }}
                                onClick={() => navigate(`/probe/${n.id}`)}
                            />
                            <text
                                x={x + 6} y={y + 14}
                                fontSize={11} fontWeight={500}
                                fill={theme.colors.ctext}
                                clipPath={`url(#cp-${rootId}-${n.id})`}
                                style={{ cursor: 'pointer', userSelect: 'none', pointerEvents: 'none' }}
                            >
                                {String(n.title || n.desc || n.id)}
                            </text>
                            <text
                                x={x + 6} y={y + 27}
                                fontSize={9.5}
                                fill={theme.colors.textMuted || '#888'}
                                style={{ userSelect: 'none', pointerEvents: 'none' }}
                            >
                                {n.op_name || ''} #{n.id}
                            </text>

                            {/* Collapse toggle — right edge, vertically centered */}
                            {n.hasChildren && (
                                <g style={{ cursor: 'pointer' }} onClick={() => onToggle(n.id)}>
                                    <rect
                                        x={x + BLOCK_W - TOGGLE_W - 2}
                                        y={y + (BLOCK_H - TOGGLE_H) / 2}
                                        width={TOGGLE_W} height={TOGGLE_H}
                                        rx={2}
                                        fill={theme.colors.bg}
                                        stroke={color}
                                        strokeWidth={1}
                                    />
                                    <text
                                        x={x + BLOCK_W - TOGGLE_W / 2 - 2}
                                        y={y + (BLOCK_H - TOGGLE_H) / 2 + TOGGLE_H - 3}
                                        textAnchor="middle"
                                        fontSize={9} fontWeight="bold"
                                        fill={theme.colors.textMuted || '#888'}
                                        style={{ userSelect: 'none' }}
                                    >
                                        {n.isCollapsed ? '▶' : '▼'}
                                    </text>
                                </g>
                            )}

                            {/* Hidden-children count to the right of collapsed node */}
                            {n.isCollapsed && n.childCount > 0 && (
                                <text
                                    x={x + BLOCK_W + 6}
                                    y={y + BLOCK_H / 2 + 4}
                                    fontSize={9.5}
                                    fill={theme.colors.textMuted || '#888'}
                                    style={{ userSelect: 'none' }}
                                >
                                    ▸ {n.childCount}
                                </text>
                            )}
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}

// ─── ProbeTree (public component) ───────────────────────────────────────────

function ProbeTree({
    forests,
    lastActiveId,
    selectedNodeId,
    focusNodeId,
    initialCollapsed,
    initialStick = true,
    onExpandNode,
}: {
    forests: Map<number, any[]>;
    lastActiveId?: number | null;
    selectedNodeId?: number | null;
    focusNodeId?: number | null;
    initialCollapsed?: Set<number>;
    initialStick?: boolean;
    onExpandNode?: (rootId: number) => Promise<void>;
}) {
    const theme: any  = useTheme();
    const navigate    = useNavigate();
    const containerRef = useRef<HTMLDivElement>(null);
    const blockRefs    = useRef(new Map<number, SVGRectElement>());
    const stickToBottomRef = useRef(initialStick);
    const stickToRightRef = useRef(initialStick);

    const [collapsed, setCollapsed] = useState<Set<number>>(() => initialCollapsed ?? new Set());

    const handleContainerScroll = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        const distanceFromRight = container.scrollWidth - container.scrollLeft - container.clientWidth;
        stickToBottomRef.current = distanceFromBottom <= 80;
        stickToRightRef.current = distanceFromRight <= 80;
    }, []);

    const onToggle = useCallback((id: number) => {
        setCollapsed(prev => {
            const next = new Set(prev);
            const wasCollapsed = next.has(id);
            if (wasCollapsed) {
                next.delete(id);
                // Node is being expanded — fetch children if callback provided
                if (onExpandNode) {
                    onExpandNode(id).catch(e => console.error('Error loading probe children:', e));
                }
            } else {
                next.add(id);
            }
            return next;
        });
    }, [onExpandNode]);

    // Keep the viewport pinned to the latest activity corner (bottom-right)
    // while the tree is actively growing. If the user scrolls away on either
    // axis, following on that axis pauses automatically.
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const rafId = requestAnimationFrame(() => {
            if (stickToBottomRef.current) {
                container.scrollTop = container.scrollHeight;
            }
            if (stickToRightRef.current) {
                container.scrollLeft = container.scrollWidth;
            }
        });
        return () => cancelAnimationFrame(rafId);
    }, [forests, collapsed]);

    useEffect(() => {
        if (!focusNodeId) return;
        const el = blockRefs.current.get(focusNodeId);
        const container = containerRef.current;
        if (!el || !container) return;

        const nRect = el.getBoundingClientRect();
        const cRect = container.getBoundingClientRect();
        container.scrollBy({
            left: nRect.left - cRect.left + nRect.width / 2 - container.clientWidth / 2,
            top: nRect.top - cRect.top + nRect.height / 2 - container.clientHeight / 2,
            behavior: 'auto',
        });
    }, [focusNodeId, forests, collapsed]);

    // Scroll active node into view within the card container when not pinned
    // to bottom-right. This avoids fighting with the follow-corner behavior.
    useEffect(() => {
        if (!lastActiveId) return;
        if (stickToBottomRef.current && stickToRightRef.current) return;
        const el = blockRefs.current.get(lastActiveId);
        const container = containerRef.current;
        if (!el || !container) return;

        const nRect = el.getBoundingClientRect();
        const cRect = container.getBoundingClientRect();
        const pad   = 48;

        const relL = nRect.left - cRect.left;
        const relT = nRect.top  - cRect.top;
        const relR = relL + nRect.width;
        const relB = relT + nRect.height;

        const fullyVisible =
            relL >= pad &&
            relT >= pad &&
            relR <= container.clientWidth  - pad &&
            relB <= container.clientHeight - pad;

        if (fullyVisible) return;

        container.scrollBy({
            left: relL + nRect.width  / 2 - container.clientWidth  / 2,
            top:  relT + nRect.height / 2 - container.clientHeight / 2,
            behavior: 'auto',
        });
    }, [lastActiveId, collapsed]);

    // Sort trees: newest (highest rootId) first
    const trees = useMemo(() => {
        const entries = Array.from(forests.entries());
        entries.sort((a, b) => b[0] - a[0]);
        return entries;
    }, [forests]);

    if (!trees.length) {
        return (
            <div style={{ padding: 16, color: theme.colors.textMuted || '#888', fontSize: '0.85em' }}>
                No probe trees yet.
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="probe-tree-scroll"
            onScroll={handleContainerScroll}
            style={{ padding: 8, overflowX: 'auto', overflowY: 'auto', height: '100%' }}
        >
            {trees.map(([rootId, nodes]) => (
                <TreeSvg
                    key={rootId}
                    rootId={rootId}
                    nodes={nodes}
                    collapsed={collapsed}
                    onToggle={onToggle}
                    lastActiveId={lastActiveId ?? null}
                    selectedNodeId={selectedNodeId ?? null}
                    blockRefs={blockRefs as React.RefObject<Map<number, SVGRectElement>>}
                    theme={theme}
                    navigate={navigate}
                />
            ))}
        </div>
    );
}

export default ProbeTree;
